import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import {
  createQuestionBankQuestionMutation,
  updateQuestionMutation,
  createQuestionMutationV2,
} from "./QuestionBankMutation";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { deleteOrganizationResourcesMutation } from "OrganizationResources/modules/OrganizationResourcesMutations";
import {
  getQuestionLibraryFeedQuery,
  getQuestionLibraryFeedQueryV2,
  getQuestionLibraryQuestionDetailsQuery,
  getQuestionLibraryQuestionDetailsQueryV2,
} from "./QuestionBankQuery";
import {
  writeQuestionLibraryFeedInCache,
  getQuestionLibraryFeedFromCache,
  getQuestionLibraryQuestionDetailsFromCache,
  writeQuestionLibraryQuestionDetailsInCache,
  getQuestionLibraryFeedFromCacheV2,
  writeQuestionLibraryFeedInCacheV2,
  getQuestionLibraryQuestionDetailsFromCacheV2,
  writeQuestionLibraryQuestionDetailsInCacheV2,
} from "QuestionBank/modules/QuestionBankGraphqlHelper";
import { getSelectedCurriculumId, getUserId } from "modules/CommonSelectors";
import {
  getAssociatedEntityVariables,
  getAssociatedEntityVariablesV2,
  getQuestionLibraryFeedQueryVariables,
  getQuestionLibraryFeedQueryVariablesV2,
  getUpdatedQuestionCacheData,
  getUpdatedQuestionCacheDataV2,
  getUpdatedQuestionLibraryFeedData,
  getUpdatedQuestionLibraryFeedDataV2,
  getReorderedSubQuestionsCacheData,
  getReorderedSubQuestionsCacheDataV2,
} from "./Utils.js";
import {
  APPROVED,
  PENDING_REVIEW,
  ALL_QUESTION,
  questionFeedFilterModalKeys,
} from "./Constants.js";
import { isMutationAndQueriesV2Enabled } from "Worksheet/modules/WorksheetUtils";
import { goToRelativeRoute } from "modules/Services";
import {
  updateActiveQuestion,
  updateQuestion,
} from "Worksheet/modules/WorksheetModules";
import {
  createQuestionMutation,
  reorderOrganizationResourceMutation,
  reorderQuestionMutation,
  deleteQuestionMutation,
} from "Worksheet/modules/WorksheetMutations";
import { getUserInfo } from "Login/modules/LoginModule";

const getFeedQuery = mutationAndQueriesV2Enabled =>
  mutationAndQueriesV2Enabled
    ? getQuestionLibraryFeedQueryV2
    : getQuestionLibraryFeedQuery;

const buildFeedVariables = (mutationAndQueriesV2Enabled, state) =>
  mutationAndQueriesV2Enabled
    ? getQuestionLibraryFeedQueryVariablesV2(state)
    : getQuestionLibraryFeedQueryVariables(state);

/**
 * @deprecated use createQuestionLibraryQuestionV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const createQuestionLibraryQuestion = ({ questionTypeId }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const curriculumProgramId = getSelectedCurriculumId(state);
    const variables = getQuestionLibraryFeedQueryVariables(state);

    try {
      const question = await client.mutate({
        mutation: createQuestionBankQuestionMutation,
        variables: {
          input: {
            questionTypeId: questionTypeId,
            status: "PENDING_REVIEW",
            organizationResource: {
              parentId: curriculumProgramId,
              parentType: "CURRICULUM_PROGRAM",
              organizationResources: [
                {
                  label: "Worksheet",
                  resourceType: "QUESTION",
                  type: "QUESTION_BANK_RESOURCE",
                },
              ],
            },
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getQuestionLibraryFeedQuery,
            variables,
          },
        ],
        context: {
          useHttpLink: true,
        },
      });
      const libraryQuestion = _.get(
        question,
        "data.content.createQuestion",
        ""
      );
      dispatch(
        updateActiveQuestionData({
          question: {
            id: _.get(libraryQuestion, "id"),
            parentEntityId: _.get(libraryQuestion, "parentEntity.id"),
          },
        })
      );
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

// Creates a question in the question library
export const createQuestionLibraryQuestionV2 = ({ questionTypeId }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const curriculumProgramId = getSelectedCurriculumId(state);
    const mutationAndQueriesV2Enabled = isMutationAndQueriesV2Enabled();
    const variables = buildFeedVariables(mutationAndQueriesV2Enabled, state);
    const feedQuery = getFeedQuery(mutationAndQueriesV2Enabled);
    const userInfo = getUserInfo();
    const orgId = userInfo?.org_id;

    try {
      const question = await client.mutate({
        mutation: createQuestionMutationV2,
        variables: {
          input: {
            parentId: orgId,
            parentType: "ORGANIZATION",
            questionTypeId,
            putAfterQuestionId: null,
            curriculumProgramId,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [{ query: feedQuery, variables }],
        context: {
          useHttpLink: true,
        },
      });
      const libraryQuestionId = _.get(
        question,
        "data.content.createQuestionV2.id",
        ""
      );
      dispatch(
        updateActiveQuestionData({
          question: {
            id: libraryQuestionId,
          },
        })
      );

      // Apply default PENDING_REVIEW status
      if (libraryQuestionId) {
        await client.mutate({
          mutation: updateQuestionMutation,
          variables: {
            input: {
              questionId: libraryQuestionId,
              questionData: {
                status: PENDING_REVIEW,
              },
            },
          },
        });
      }
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * @deprecated use createQuestionLibrarySubQuestionV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const createQuestionLibrarySubQuestion = ({
  parentResourceId,
  questionTypeId,
  organizationResources,
  putAfterItemId = null,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const curriculumProgramId = getSelectedCurriculumId(state);
    const variables = getQuestionLibraryFeedQueryVariables(state);
    const questionResourceId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.parentEntityId",
      ""
    );

    if (!_.isNil(curriculumProgramId)) {
      dispatch(setLoadingState(true));

      try {
        const res = await client.mutate({
          mutation: createQuestionMutation,
          variables: {
            input: {
              questionTypeId,
              organizationResource: {
                parentId: curriculumProgramId,
                parentResourceId,
                parentType: "CURRICULUM_PROGRAM",
                organizationResources,
                shouldHandleSequence: true,
                putAfterItemId,
              },
            },
          },
          refetchQueries: [
            {
              query: getQuestionLibraryFeedQuery,
              variables,
            },
            {
              query: getQuestionLibraryQuestionDetailsQuery,
              variables: {
                id: questionResourceId,
              },
            },
          ],
          awaitRefetchQueries: true,
        });
        const newQuestionId = _.get(res, "data.content.createQuestion.id", "");

        dispatch(updateActiveQuestion(newQuestionId));
      } catch (e) {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      } finally {
        dispatch(setLoadingState(false));
      }
    }
  };
};

// Creates a sub-question in the question library
export const createQuestionLibrarySubQuestionV2 = ({
  questionTypeId,
  putAfterItemId = null,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const curriculumProgramId = getSelectedCurriculumId(state);
    const mutationAndQueriesV2Enabled = isMutationAndQueriesV2Enabled();
    const variables = buildFeedVariables(mutationAndQueriesV2Enabled, state);
    const feedQuery = getFeedQuery(mutationAndQueriesV2Enabled);
    const questionId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.id",
      ""
    );

    if (!_.isNil(curriculumProgramId)) {
      dispatch(setLoadingState(true));

      try {
        const res = await client.mutate({
          mutation: createQuestionMutationV2,
          variables: {
            input: {
              parentId: questionId,
              parentType: "QUESTION",
              questionTypeId,
              putAfterQuestionId: putAfterItemId,
              curriculumProgramId,
            },
          },
          refetchQueries: [
            {
              query: feedQuery,
              variables,
            },
            {
              query: getQuestionLibraryQuestionDetailsQueryV2,
              variables: {
                id: questionId,
              },
            },
          ],
          awaitRefetchQueries: true,
        });
        const newQuestionId = _.get(
          res,
          "data.content.createQuestionV2.id",
          ""
        );

        dispatch(updateActiveQuestion(newQuestionId));
      } catch (e) {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      } finally {
        dispatch(setLoadingState(false));
      }
    }
  };
};

/**
 * @deprecated use createLibraryQuestionFieldV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const createLibraryQuestionField = ({ uid, questionId }) => {
  return async (dispatch, getState) => {
    dispatch(setLoadingState(true));
    const state = getState();
    const questionResourceId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.parentEntityId",
      ""
    );

    try {
      await dispatch(
        updateQuestion({
          id: questionId,
          fields: [{ uid }],
          refetchQueries: [
            {
              query: getQuestionLibraryQuestionDetailsQuery,
              variables: {
                id: questionResourceId,
              },
            },
          ],
          awaitRefetchQueries: true,
        })
      );
    } catch {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    } finally {
      dispatch(setLoadingState(false));
    }
  };
};

export const createLibraryQuestionFieldV2 = ({ uid, questionId }) => {
  return async (dispatch, getState) => {
    dispatch(setLoadingState(true));
    const state = getState();
    const activeQuestionId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.id",
      ""
    );

    try {
      await dispatch(
        updateQuestion({
          id: questionId,
          fields: [{ uid }],
          refetchQueries: [
            {
              query: getQuestionLibraryQuestionDetailsQueryV2,
              variables: {
                id: activeQuestionId,
              },
            },
          ],
          awaitRefetchQueries: true,
        })
      );
    } catch {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    } finally {
      dispatch(setLoadingState(false));
    }
  };
};

export const deleteOrganizationResource = ({
  id,
  showToastMsg,
  isSubQuestion = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const variables = getQuestionLibraryFeedQueryVariables(state);

    let index = -1,
      parentIndex;

    const libraryQuestionDetails = getQuestionLibraryFeedFromCache(variables);

    if (!_.isEmpty(id)) {
      if (isSubQuestion) {
        parentIndex = _.findIndex(
          _.get(
            libraryQuestionDetails,
            "node.organizationResourcesV2.edges",
            []
          ),
          question => {
            const subQuestionIndex = _.findIndex(
              _.get(question, "node.childrenV2.edges", []),
              edge => _.get(edge, "node.id") === id
            );
            if (subQuestionIndex !== -1) {
              index = subQuestionIndex;
              return true;
            }
            return false;
          }
        );
      } else {
        index = _.findIndex(
          _.get(
            libraryQuestionDetails,
            "node.organizationResourcesV2.edges",
            []
          ),
          ["node.id", id]
        );
      }

      if (index != -1) {
        let updateData;
        if (isSubQuestion) {
          updateData = update(libraryQuestionDetails, {
            node: {
              organizationResourcesV2: {
                edges: {
                  [parentIndex]: {
                    node: {
                      childrenV2: {
                        edges: {
                          $splice: [[index, 1]],
                        },
                      },
                    },
                  },
                },
              },
            },
          });
        } else {
          updateData = update(libraryQuestionDetails, {
            node: {
              organizationResourcesV2: {
                edges: {
                  $splice: [[index, 1]],
                },
              },
            },
          });
        }

        writeQuestionLibraryFeedInCache({
          variables,
          data: updateData,
        });
      }

      try {
        await client.mutate({
          mutation: deleteOrganizationResourcesMutation,
          variables: {
            ids: [id],
          },
          refetchQueries: [
            {
              query: getQuestionLibraryFeedQuery,
              variables,
            },
          ],
        });
        showToastMsg &&
          dispatch(
            setToastMsg({
              type: "success",
              msg: "toastMsgs:successfully_with_label",
              locale_params: [
                {
                  key: "label",
                  value: "toastMsgs:deleted",
                  isPlainText: false,
                },
              ],
            })
          );
      } catch (error) {
        writeQuestionLibraryFeedInCache({
          variables,
          data: libraryQuestionDetails,
        });

        if (error.networkError) {
          dispatch(setToastMsg("toastMsgs:no_internet_connection"));
        } else {
          dispatch(setToastMsg("toastMsgs:something_went_wrong"));
        }
      }
    }
  };
};

export const deleteQuestionLibraryQuestionV2 = ({ questionId }) => {
  return async (dispatch, getState) => {
    if (_.isEmpty(questionId)) return;

    const state = getState();
    const variables = getQuestionLibraryFeedQueryVariablesV2(state);
    const feedCacheData = getQuestionLibraryFeedFromCacheV2(variables);

    if (!_.isEmpty(feedCacheData)) {
      const updatedEdges = _.filter(
        _.get(feedCacheData, "node.questions.edges", []),
        edge => _.get(edge, "id") !== questionId
      );
      const updatedFeedData = update(feedCacheData, {
        node: { questions: { edges: { $set: updatedEdges } } },
      });
      writeQuestionLibraryFeedInCacheV2({ variables, data: updatedFeedData });
    }

    try {
      await client.mutate({
        mutation: deleteQuestionMutation,
        variables: { id: questionId },
        refetchQueries: [{ query: getQuestionLibraryFeedQueryV2, variables }],
      });
    } catch {
      writeQuestionLibraryFeedInCacheV2({ variables, data: feedCacheData });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteQuestionLibrarySubQuestionV2 = ({ questionId }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const parentQuestionId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.id",
      ""
    );

    const cacheData = getQuestionLibraryQuestionDetailsFromCacheV2({
      id: parentQuestionId,
    });

    const updatedEdges = _.filter(
      _.get(cacheData, "children.edges", []),
      edge => _.get(edge, "id") !== questionId
    );

    const updatedCacheData = update(cacheData, {
      children: { edges: { $set: updatedEdges } },
    });

    writeQuestionLibraryQuestionDetailsInCacheV2({
      id: parentQuestionId,
      data: { node: updatedCacheData },
    });

    try {
      await client.mutate({
        mutation: deleteQuestionMutation,
        variables: { id: questionId },
      });
    } catch {
      writeQuestionLibraryQuestionDetailsInCacheV2({
        id: parentQuestionId,
        data: { node: cacheData },
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateQuestionLibraryQuestion = ({
  status = null,
  isArchived = null,
  params = null,
} = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userId = getUserId(state);
    const questionId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.id"
    );
    const questionResourceId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.parentEntityId",
      ""
    );
    const questionLibraryFeedVariables = getQuestionLibraryFeedQueryVariables(
      state
    );
    const feedType = _.get(state, "questionLibrary.feedType");
    const filteredQuestionCount = _.get(
      state,
      "questionLibrary.questionCount.filteredQuestionCount",
      0
    );

    const cacheData = getQuestionLibraryQuestionDetailsFromCache({
      id: questionResourceId,
    });

    const questionLibraryFeedCacheData = getQuestionLibraryFeedFromCache(
      questionLibraryFeedVariables
    );

    const activeQuestionFilters = _.get(
      state,
      "questionLibrary.activeQuestionFilters",
      {}
    );
    const newAssociatedEntities = getAssociatedEntityVariables(
      cacheData,
      activeQuestionFilters
    );

    const inputQuestionData = {};

    const difficulty = _.get(activeQuestionFilters, "difficulty", null);
    if (difficulty) {
      inputQuestionData.difficulty = difficulty;
    }

    if (!_.isEmpty(status)) {
      inputQuestionData.status = status;
      if (status === APPROVED) {
        inputQuestionData.reviewer = userId;
      }
    }
    if (_.isBoolean(isArchived)) {
      inputQuestionData.isArchived = isArchived;
    }

    const variables = {
      input: {},
    };

    if (!_.isEmpty(inputQuestionData)) {
      variables.input["questionData"] = inputQuestionData;
    }

    if (
      !_.isEmpty(newAssociatedEntities?.addedAssociatedEntities) ||
      !_.isEmpty(newAssociatedEntities?.removedAssociatedEntities)
    ) {
      variables.input["associatedEntities"] = newAssociatedEntities;
    }

    const updatedQuestionData = getUpdatedQuestionCacheData(
      cacheData,
      variables
    );

    const {
      updatedQuestionLibraryFeedData,
      isUpdateQuestionFeedCatchData,
    } = getUpdatedQuestionLibraryFeedData({
      updatedQuestionData,
      questionLibraryFeedCacheData,
      feedType,
      filteredQuestionCount,
    });

    if (!_.isEmpty(variables?.input) && questionId) {
      variables["input"]["questionId"] = questionId;

      if (isUpdateQuestionFeedCatchData || !_.isEmpty(params)) {
        // check params to go to next question when feedType is All question and we are not updating questionLibraryFeed cache
        dispatch(goToNextQuestion({ params }));
      }

      writeQuestionLibraryFeedInCache({
        variables: questionLibraryFeedVariables,
        data: updatedQuestionLibraryFeedData,
      });

      writeQuestionLibraryQuestionDetailsInCache({
        id: questionResourceId,
        data: updatedQuestionData,
      });

      try {
        await client.mutate({
          mutation: updateQuestionMutation,
          variables,
        });
      } catch (e) {
        /** revert changes */
        writeQuestionLibraryQuestionDetailsInCache({
          id: questionResourceId,
          data: { node: cacheData },
        });
        writeQuestionLibraryFeedInCache({
          variables: questionLibraryFeedVariables,
          data: questionLibraryFeedCacheData,
        });
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const updateQuestionLibraryQuestionV2 = ({
  status = null,
  isArchived = null,
  params = null,
} = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userId = getUserId(state);
    const questionId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.id"
    );
    const questionLibraryFeedVariables = getQuestionLibraryFeedQueryVariablesV2(
      state
    );
    const feedType = _.get(state, "questionLibrary.feedType");
    const filteredQuestionCount = _.get(
      state,
      "questionLibrary.questionCount.filteredQuestionCount",
      0
    );

    const cacheData = getQuestionLibraryQuestionDetailsFromCacheV2({
      id: questionId,
    });

    const questionLibraryFeedCacheData = getQuestionLibraryFeedFromCacheV2(
      questionLibraryFeedVariables
    );

    const activeQuestionFilters = _.get(
      state,
      "questionLibrary.activeQuestionFilters",
      {}
    );
    const newAssociatedEntities = getAssociatedEntityVariablesV2(
      cacheData,
      activeQuestionFilters
    );

    const inputQuestionData = {};

    const difficulty = _.get(activeQuestionFilters, "difficulty", null);
    if (difficulty) {
      inputQuestionData.difficulty = difficulty;
    }

    if (!_.isEmpty(status)) {
      inputQuestionData.status = status;
      if (status === APPROVED) {
        inputQuestionData.reviewer = userId;
      }
    }
    if (_.isBoolean(isArchived)) {
      inputQuestionData.isArchived = isArchived;
    }

    const variables = {
      input: {},
    };

    if (!_.isEmpty(inputQuestionData)) {
      variables.input["questionData"] = inputQuestionData;
    }

    if (
      !_.isEmpty(newAssociatedEntities?.addedAssociatedEntities) ||
      !_.isEmpty(newAssociatedEntities?.removedAssociatedEntities)
    ) {
      variables.input["associatedEntities"] = newAssociatedEntities;
    }

    const updatedQuestionData = getUpdatedQuestionCacheDataV2(
      cacheData,
      variables
    );

    const {
      updatedQuestionLibraryFeedData,
      isUpdateQuestionFeedCatchData,
    } = getUpdatedQuestionLibraryFeedDataV2({
      updatedQuestionData,
      questionLibraryFeedCacheData,
      feedType,
      filteredQuestionCount,
    });

    if (!_.isEmpty(variables?.input) && questionId) {
      variables["input"]["questionId"] = questionId;

      if (isUpdateQuestionFeedCatchData || !_.isEmpty(params)) {
        dispatch(goToNextQuestionV2({ params }));
      }

      writeQuestionLibraryFeedInCacheV2({
        variables: questionLibraryFeedVariables,
        data: updatedQuestionLibraryFeedData,
      });

      writeQuestionLibraryQuestionDetailsInCacheV2({
        id: questionId,
        data: updatedQuestionData,
      });

      try {
        await client.mutate({
          mutation: updateQuestionMutation,
          variables,
        });
      } catch {
        writeQuestionLibraryQuestionDetailsInCacheV2({
          id: questionId,
          data: { node: cacheData },
        });
        writeQuestionLibraryFeedInCacheV2({
          variables: questionLibraryFeedVariables,
          data: questionLibraryFeedCacheData,
        });
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const deleteLibraryQuestionField = ({
  ids = [],
  isAnswerField = false,
  questionId,
  isSubQuestion = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const questionResourceId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.parentEntityId",
      ""
    );

    const variables = {
      input: {
        questionId,
        removedFields: ids,
      },
    };
    const questionData = getQuestionLibraryQuestionDetailsFromCache({
      id: questionResourceId,
    });

    let updatedQuestionData = _.cloneDeep(questionData);

    const fieldsKey = isAnswerField ? "answerFields" : "fields";
    const fields = _.get(questionData, ["item", fieldsKey], []);

    if (isSubQuestion) {
      // Map through sub-questions and update the fields
      const updatedEdges = _.get(questionData, "childrenV2.edges", []).map(
        edge => {
          const subQuestionFields = _.get(edge, "node.item.fields", []);
          const updatedSubQuestionFields = _.filter(
            subQuestionFields,
            field => !_.includes(ids, _.get(field, "id"))
          );
          return update(edge, {
            node: {
              item: {
                [fieldsKey]: { $set: updatedSubQuestionFields },
              },
            },
          });
        }
      );
      // Apply the updated edges to the main question data
      updatedQuestionData = update(updatedQuestionData, {
        childrenV2: {
          edges: { $set: updatedEdges },
        },
      });
    } else {
      const updatedFields = _.filter(
        fields,
        field => !_.includes(ids, _.get(field, "id"))
      );

      updatedQuestionData = update(updatedQuestionData, {
        item: {
          [fieldsKey]: { $set: updatedFields },
        },
      });
    }

    writeQuestionLibraryQuestionDetailsInCache({
      id: questionResourceId,
      data: { node: updatedQuestionData },
    });

    try {
      await client.mutate({
        mutation: updateQuestionMutation,
        variables,
      });
    } catch (e) {
      writeQuestionLibraryQuestionDetailsInCache({
        id: questionResourceId,
        data: { node: questionData },
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteLibraryQuestionFieldV2 = ({
  ids = [],
  isAnswerField = false,
  questionId,
  isSubQuestion = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const activeQuestionId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.id",
      ""
    );

    const variables = {
      input: {
        questionId,
        removedFields: ids,
      },
    };
    const questionData = getQuestionLibraryQuestionDetailsFromCacheV2({
      id: activeQuestionId,
    });

    let updatedQuestionData = _.cloneDeep(questionData);

    const fieldsKey = isAnswerField ? "answerFields" : "fields";

    if (isSubQuestion) {
      const updatedEdges = _.get(questionData, "children.edges", []).map(
        edge => {
          const subQuestionFields = _.get(edge, "fields", []);
          const updatedSubQuestionFields = _.filter(
            subQuestionFields,
            field => !_.includes(ids, _.get(field, "id"))
          );
          return update(edge, {
            [fieldsKey]: { $set: updatedSubQuestionFields },
          });
        }
      );
      updatedQuestionData = update(updatedQuestionData, {
        children: {
          edges: { $set: updatedEdges },
        },
      });
    } else {
      const fields = _.get(questionData, fieldsKey, []);
      const updatedFields = _.filter(
        fields,
        field => !_.includes(ids, _.get(field, "id"))
      );
      updatedQuestionData = update(updatedQuestionData, {
        [fieldsKey]: { $set: updatedFields },
      });
    }

    writeQuestionLibraryQuestionDetailsInCacheV2({
      id: activeQuestionId,
      data: { node: updatedQuestionData },
    });

    try {
      await client.mutate({
        mutation: updateQuestionMutation,
        variables,
      });
    } catch {
      writeQuestionLibraryQuestionDetailsInCacheV2({
        id: activeQuestionId,
        data: { node: questionData },
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const goToQuestion = ({ params, questionResourceId }) => {
  return async dispatch => {
    const isQueriesV2Enabled = isMutationAndQueriesV2Enabled();
    const paramKey = isQueriesV2Enabled
      ? "question_id"
      : "question_resource_id";
    const currentParamId = _.get(params, paramKey);

    if (currentParamId !== questionResourceId) {
      const route = currentParamId
        ? `../${questionResourceId}`
        : `${questionResourceId}`;
      const type = currentParamId ? "replace" : "push";

      dispatch(goToRelativeRoute({ route, type }));
    }
  };
};

export const goToQuestionFeed = ({ params }) => {
  return async dispatch => {
    const isQueriesV2Enabled = isMutationAndQueriesV2Enabled();
    const paramKey = isQueriesV2Enabled
      ? "question_id"
      : "question_resource_id";
    if (_.get(params, paramKey, null)) {
      dispatch(resetActiveQuestionData());
      dispatch(
        goToRelativeRoute({
          route: `../`,
          type: "replace",
        })
      );
    }
  };
};

export const goToNextQuestion = ({ params }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const questionResourceId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.parentEntityId",
      ""
    );

    const variables = getQuestionLibraryFeedQueryVariables(state);
    const questionLibraryFeedDetails = getQuestionLibraryFeedFromCache(
      variables
    );
    const questionLibraryFeedData = _.get(
      questionLibraryFeedDetails,
      "node.organizationResourcesV2.edges",
      []
    );

    const currentQuestionResourceIndex = _.findIndex(
      questionLibraryFeedData,
      question => _.get(question, "node.id") === questionResourceId
    );

    if (
      currentQuestionResourceIndex >= 0 &&
      currentQuestionResourceIndex < _.size(questionLibraryFeedData) - 1
    ) {
      const nextQuestionResourceId = _.get(
        questionLibraryFeedData[currentQuestionResourceIndex + 1],
        "node.id"
      );

      dispatch(
        goToQuestion({ params, questionResourceId: nextQuestionResourceId })
      );
    } else if (currentQuestionResourceIndex - 1 >= 0) {
      const previousQuestionResourceId = _.get(
        questionLibraryFeedData[currentQuestionResourceIndex - 1],
        "node.id"
      );

      dispatch(
        goToQuestion({ params, questionResourceId: previousQuestionResourceId })
      );
    } else {
      dispatch(goToQuestionFeed({ params }));
    }
  };
};

export const goToNextQuestionV2 = ({ params }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const questionId = _.get(
      state,
      "questionLibrary.activeQuestionData.question.id",
      ""
    );

    const feedVariables = getQuestionLibraryFeedQueryVariablesV2(state);
    const questionLibraryFeedDetails = getQuestionLibraryFeedFromCacheV2(
      feedVariables
    );
    const feedEdges = _.get(
      questionLibraryFeedDetails,
      "node.questions.edges",
      []
    );

    const currentIndex = _.findIndex(
      feedEdges,
      edge => _.get(edge, "id") === questionId
    );

    if (currentIndex >= 0 && currentIndex < _.size(feedEdges) - 1) {
      const nextId = _.get(feedEdges[currentIndex + 1], "id");
      dispatch(goToQuestion({ params, questionResourceId: nextId }));
    } else if (currentIndex - 1 >= 0) {
      const prevId = _.get(feedEdges[currentIndex - 1], "id");
      dispatch(goToQuestion({ params, questionResourceId: prevId }));
    } else {
      dispatch(goToQuestionFeed({ params }));
    }
  };
};

/** Redux */

export const NAME = "questionLibrary";
const RESET_STATE = `RESET_STATE ${NAME}`;
const CHANGE_LOADING_STATE = `CHANGE_LOADING_STATE ${NAME}`;
const RESET_ACTIVE_QUESTION_FILTERS = `RESET_ACTIVE_QUESTION_FILTERS ${NAME}`;
const RESET_FILTERS_MODAL_FILTERS = `RESET_FILTERS_MODAL_FILTERS ${NAME}`;
const UPDATE_ACTIVE_QUESTION_FILTERS = `UPDATE_ACTIVE_QUESTION_FILTERS ${NAME}`;
const UPDATE_FEED_FILTERS = `UPDATE_FEED_FILTERS ${NAME}`;
const UPDATE_QUESTION_COUNT = `UPDATE_QUESTION_COUNT ${NAME}`;
const UPDATE_FEED_TYPE = `UPDATE_FEED_TYPE ${NAME}`;
const UPDATE_QUESTION_TYPE = `UPDATE_QUESTION_TYPE ${NAME}`;
const RESET_FEED_FILTERS = `RESET_FEED_FILTERS${NAME}`;
const UPDATE_ACTIVE_QUESTION_DATA = `UPDATE_ACTIVE_QUESTION_DATA ${NAME}`;
const RESET_ACTIVE_QUESTION_DATA = `RESET_ACTIVE_QUESTION_DATA ${NAME}`;

export const updateQuestionFeedFilters = payload => {
  return { type: UPDATE_FEED_FILTERS, payload };
};

export const updateActiveQuestionFilters = payload => {
  return { type: UPDATE_ACTIVE_QUESTION_FILTERS, payload };
};

export const resetActiveQuestionFilters = () => {
  return { type: RESET_ACTIVE_QUESTION_FILTERS };
};

export const resetFiltersModalFilters = () => {
  return { type: RESET_FILTERS_MODAL_FILTERS };
};

export const resetActiveQuestionData = () => {
  return { type: RESET_ACTIVE_QUESTION_DATA };
};

export const resetState = () => {
  return { type: RESET_STATE };
};

export const setLoadingState = payload => {
  return { type: CHANGE_LOADING_STATE, payload };
};

export const updateQuestionCount = payload => {
  return { type: UPDATE_QUESTION_COUNT, payload };
};

export const updateFeedType = payload => {
  return { type: UPDATE_FEED_TYPE, payload };
};

export const resetFeedFilters = () => {
  return { type: RESET_FEED_FILTERS };
};
export const updateActiveQuestionData = payload => {
  return { type: UPDATE_ACTIVE_QUESTION_DATA, payload };
};

const initialState = {
  feedFilters: {
    subjects: null,
    grades: null,
    questionTypes: null,
    createdBy: null,
    difficulty: null,
    status: null,
    reviewer: null,
    paperTypes: null,
    isArchived: null,
    plannerElements: null,
    objectives: null,
    topics: [],
    subtopics: [],
    contents: [],
    searchText: "",
    criterion: null,
    keyConcepts: null,
    globalContext: null,
    relatedConcepts: null,
    subjectLevels: null,
  },
  activeQuestionFilters: {
    subject: null,
    grades: null,
    difficulty: null,
    paperTypes: null,
    plannerElements: null,
    objectives: null,
    criterion: null,
    keyConcepts: null,
    globalContext: null,
    relatedConcepts: null,
    subjectLevels: null,
  },
  questionCount: {
    totalQuestionCount: 0,
    filteredQuestionCount: 0,
    questionStatusWiseCount: 0,
  },
  isLoading: false,
  feedType: ALL_QUESTION,
  activeQuestionData: {
    question: {},
    selectedQuestionTypeId: null,
    preserveQuestionDetails: false /** boolean used to preserve active question's tagging details and create another question with the same data */,
  },
  questionType: "",
};

const REDUCER_HANDLERS = {
  [UPDATE_FEED_FILTERS]: (state, action) => {
    const params = action.payload;

    _.map(_.keys(params), key => {
      state = update(state, {
        feedFilters: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [UPDATE_ACTIVE_QUESTION_FILTERS]: (state, action) => {
    const params = action.payload;

    _.map(_.keys(params), key => {
      state = update(state, {
        activeQuestionFilters: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [RESET_FILTERS_MODAL_FILTERS]: state => {
    _.map(questionFeedFilterModalKeys, key => {
      state = update(state, {
        feedFilters: { [key]: { $set: initialState.feedFilters[key] } },
      });
    });

    return state;
  },
  [RESET_ACTIVE_QUESTION_FILTERS]: state => {
    return update(state, {
      activeQuestionFilters: { $set: initialState.activeQuestionFilters },
    });
  },
  [RESET_FEED_FILTERS]: state => {
    return update(state, {
      feedFilters: { $set: initialState.feedFilters },
    });
  },
  [RESET_ACTIVE_QUESTION_DATA]: state => {
    return update(state, {
      activeQuestionData: { $set: initialState.activeQuestionData },
    });
  },
  [RESET_STATE]: state => {
    return update(state, { $set: initialState });
  },
  [CHANGE_LOADING_STATE]: (state, action) => {
    return update(state, { isLoading: { $set: action.payload } });
  },
  [UPDATE_FEED_TYPE]: (state, action) => {
    return update(state, { feedType: { $set: action.payload } });
  },
  [UPDATE_QUESTION_TYPE]: (state, action) => {
    return update(state, { questionType: { $set: action.payload } });
  },

  [UPDATE_ACTIVE_QUESTION_DATA]: (state, action) => {
    const params = action.payload;

    _.map(_.keys(params), key => {
      state = update(state, {
        activeQuestionData: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [UPDATE_QUESTION_COUNT]: (state, action) => {
    const totalQuestionCount = _.get(
      action,
      "payload.totalQuestionCount",
      state.questionCount.totalQuestionCount
    );
    const filteredQuestionCount = _.get(
      action,
      "payload.filteredQuestionCount",
      state.questionCount.filteredQuestionCount
    );
    const questionStatusWiseCount = _.get(
      action,
      "payload.questionStatusWiseCount",
      state.questionCount.questionStatusWiseCount
    );
    return update(state, {
      questionCount: {
        totalQuestionCount: { $set: totalQuestionCount },
        filteredQuestionCount: { $set: filteredQuestionCount },
        questionStatusWiseCount: { $set: questionStatusWiseCount },
      },
    });
  },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}

export const reorderSubQuestionInQuestionBank = ({
  parentResourceId,
  questionResourceId,
  fromIndex,
  toIndex,
  putAfterItemId,
}) => {
  return async dispatch => {
    const cacheData = getQuestionLibraryQuestionDetailsFromCache({
      id: parentResourceId,
    });

    const updatedCacheData = getReorderedSubQuestionsCacheData({
      cacheData,
      fromIndex,
      toIndex,
    });

    writeQuestionLibraryQuestionDetailsInCache({
      id: parentResourceId,
      data: { node: updatedCacheData },
    });

    try {
      await client.mutate({
        mutation: reorderOrganizationResourceMutation,
        variables: {
          id: questionResourceId,
          putAfterItemId,
          switchParent: false,
          parentResourceId,
        },
      });
    } catch {
      writeQuestionLibraryQuestionDetailsInCache({
        id: parentResourceId,
        data: { node: cacheData },
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const reorderSubQuestionInQuestionBankV2 = ({
  parentQuestionId,
  questionId,
  fromIndex,
  toIndex,
  putAfterQuestionId,
}) => {
  return async dispatch => {
    const cacheData = getQuestionLibraryQuestionDetailsFromCacheV2({
      id: parentQuestionId,
    });

    const updatedCacheData = getReorderedSubQuestionsCacheDataV2({
      cacheData,
      fromIndex,
      toIndex,
    });

    writeQuestionLibraryQuestionDetailsInCacheV2({
      id: parentQuestionId,
      data: { node: updatedCacheData },
    });

    try {
      await client.mutate({
        mutation: reorderQuestionMutation,
        variables: {
          questionId,
          putAfterQuestionId,
          parentId: parentQuestionId,
        },
        refetchQueries: [
          {
            query: getQuestionLibraryQuestionDetailsQueryV2,
            variables: { id: parentQuestionId },
          },
        ],
        awaitRefetchQueries: true,
      });
    } catch {
      writeQuestionLibraryQuestionDetailsInCacheV2({
        id: parentQuestionId,
        data: { node: cacheData },
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};
